import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  TextField,
  Checkbox,
  Badge,
} from '@shopify/polaris';
import { CartDrawerSettings } from '../types';

interface Props {
  settings: CartDrawerSettings;
  onChange: (settings: CartDrawerSettings) => void;
}

export function CartDrawerSettingsForm({ settings, onChange }: Props) {
  const { t } = useTranslation();
  const update = useCallback(
    (patch: Partial<CartDrawerSettings>) => onChange({ ...settings, ...patch }),
    [settings, onChange]
  );

  return (
    <BlockStack gap="400">
      {/* Enable / Disable */}
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between">
            <Text variant="headingMd" as="h2">
              🛒 {t('cartDrawer.title')}
            </Text>
            <Badge tone={settings.enabled ? 'success' : 'new'}>
              {settings.enabled ? t('common.active') : t('common.inactive')}
            </Badge>
          </InlineStack>
          <Text as="p" tone="subdued">
            {t('cartDrawer.description')}
          </Text>
          <Checkbox
            label={t('cartDrawer.enableLabel')}
            checked={settings.enabled}
            onChange={(v) => update({ enabled: v })}
            helpText={t('cartDrawer.enableHelp')}
          />
        </BlockStack>
      </Card>

      {settings.enabled && (
        <>
          {/* General Texts */}
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">{t('cartDrawer.texts.title')}</Text>
              <Text as="p" tone="subdued">
                {t('cartDrawer.texts.description')}
              </Text>
              <TextField
                label={t('cartDrawer.texts.cartTitleLabel')}
                value={settings.cartTitle}
                onChange={(v) => update({ cartTitle: v })}
                autoComplete="off"
              />
              <TextField
                label={t('cartDrawer.texts.emptyTextLabel')}
                value={settings.emptyText}
                onChange={(v) => update({ emptyText: v })}
                autoComplete="off"
              />
              <TextField
                label={t('cartDrawer.texts.continueTextLabel')}
                value={settings.continueText}
                onChange={(v) => update({ continueText: v })}
                autoComplete="off"
              />
              <TextField
                label={t('cartDrawer.texts.subtotalTextLabel')}
                value={settings.subtotalText}
                onChange={(v) => update({ subtotalText: v })}
                autoComplete="off"
              />
              <TextField
                label={t('cartDrawer.texts.checkoutTextLabel')}
                value={settings.checkoutText}
                onChange={(v) => update({ checkoutText: v })}
                autoComplete="off"
              />
              <TextField
                label={t('cartDrawer.texts.shippingNoteLabel')}
                value={settings.shippingNote}
                onChange={(v) => update({ shippingNote: v })}
                autoComplete="off"
              />
            </BlockStack>
          </Card>

          {/* Upsell */}
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">{t('cartDrawer.upsell.title')}</Text>
              <Text as="p" tone="subdued">
                {t('cartDrawer.upsell.description')}
              </Text>
              <TextField
                label={t('cartDrawer.upsell.upsellTitleLabel')}
                value={settings.upsellTitle}
                onChange={(v) => update({ upsellTitle: v })}
                autoComplete="off"
              />
            </BlockStack>
          </Card>

          {/* Free Shipping Bar */}
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">{t('cartDrawer.freeShipping.title')}</Text>
              <Text as="p" tone="subdued">
                {t('cartDrawer.freeShipping.description')}
              </Text>
              <TextField
                label={t('cartDrawer.freeShipping.thresholdLabel')}
                type="number"
                value={String(settings.freeShippingThreshold)}
                onChange={(v) => update({ freeShippingThreshold: Number(v) })}
                helpText={t('cartDrawer.freeShipping.thresholdHelp')}
                autoComplete="off"
              />
              {settings.freeShippingThreshold > 0 && (
                <>
                  <TextField
                    label={t('cartDrawer.freeShipping.textLabel')}
                    value={settings.freeShippingText}
                    onChange={(v) => update({ freeShippingText: v })}
                    autoComplete="off"
                  />
                  <TextField
                    label={t('cartDrawer.freeShipping.moreLabel')}
                    value={settings.freeShippingMore}
                    onChange={(v) => update({ freeShippingMore: v })}
                    autoComplete="off"
                  />
                  <TextField
                    label={t('cartDrawer.freeShipping.achievedLabel')}
                    value={settings.freeShippingAchieved}
                    onChange={(v) => update({ freeShippingAchieved: v })}
                    autoComplete="off"
                  />
                </>
              )}
            </BlockStack>
          </Card>

          {/* Behavior */}
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">{t('cartDrawer.behavior.title')}</Text>
              <Text as="p" tone="subdued">
                {t('cartDrawer.behavior.description')}
              </Text>
              <Checkbox
                label={t('cartDrawer.behavior.interceptLabel')}
                checked={settings.interceptAddToCart}
                onChange={(v) => update({ interceptAddToCart: v })}
                helpText={t('cartDrawer.behavior.interceptHelp')}
              />
              <Checkbox
                label={t('cartDrawer.behavior.openOnAddLabel')}
                checked={settings.openOnAdd}
                onChange={(v) => update({ openOnAdd: v })}
                helpText={t('cartDrawer.behavior.openOnAddHelp')}
              />
            </BlockStack>
          </Card>
        </>
      )}
    </BlockStack>
  );
}
